import { EarthPressuresCalculation } from "../earth-pressures/EarthPressuresCalculation.js";
import { EarthPressuresData } from "../earth-pressures/EarthPressuresData.js";
import { EarthPressuresModelInterval } from "../earth-pressures/EarthPressuresModelInterval.js";
import { FacePressuresCalculation } from "../face-pressures/FacePressuresCalculation.js";
import { FacePressuresForcesAtAngle } from "../face-pressures/FacePressuresForcesAtAngle.js";
import { FacePressuresForcesData } from "../face-pressures/FacePressuresForcesData.js";
import { FacePressuresGeotechData } from "../face-pressures/FacePressuresGeotechData.js";
import { FacePressuresModelInterval } from "../face-pressures/FacePressuresModelInterval.js";
import { FacePressuresPressuresAtLevel } from "../face-pressures/FacePressuresPressuresAtLevel.js";
import { GeoLayer } from "../geo-sections/GeoLayer.js";
import { GeoParams } from "../geo-sections/GeoParams.js";
import { GeoSection } from "../geo-sections/GeoSection.js";
import { GeoType } from "../geo-sections/GeoType.js";
import { WaterBody } from "../geo-sections/WaterBody.js";
import { GeoBoundary } from "../model-sections/GeoBoundary.js";
import { GeoLoad } from "../model-sections/GeoLoad.js";
import { GeoObject } from "../model-sections/GeoObject.js";
import { GeoObjectTbm } from "../model-sections/GeoObjectTbm.js";
import { GeoObjectTbmBoundary } from "../model-sections/GeoObjectTbmBoundary.js";
import { GeoObjectTbmSensor } from "../model-sections/GeoObjectTbmSensor.js";
import { ModelInterval } from "../model-sections/ModelInterval.js";
import { ModelSection } from "../model-sections/ModelSection.js";

const TBM_OBJECT_TYPE = 'tbm';

const GEO_TYPE_PROPS = [
    'id', 'uid', 'name', 'effectiveCohesionCef', 'effectiveInternalFrictionAngleFiEf', 'poissonCoefficientNi',
    'overconsolidationRatioOcr', 'bulkUnitWeightGama', 'bulkUnitWeightGamaGiven', 'minBulkUnitWeightGamaMin',
    'minBulkUnitWeightGamaMinGiven', 'dryUnitWeightGamaD', 'dryUnitWeightGamaDGiven', 'specificGravityGs',
    'unitWeightOfSolidsGamaS', 'unitWeightOfSolidsGamaSGiven', 'minUnitWeightOfSolidsGamaSMin',
    'minUnitWeightOfSolidsGamaSMinGiven', 'saturatedUnitWeightGamaSat', 'saturatedUnitWeightGamaSatGiven',
    'submergedUnitWeightGamaSub', 'submergedUnitWeightGamaSubGiven', 'minSubmergedUnitWeightGamaSubMin',
    'minSubmergedUnitWeightGamaSubMinGiven', 'hasBuoyancyEffectWhenUnderWater', 'degreeOfSaturationSr',
    'degreeOfSaturationSrGiven', 'moistureContentW', 'moistureContentWGiven', 'plasticityIndexIp',
    'plasticityIndexIpGiven', 'porosityN', 'porosityNGiven', 'voidRatioE', 'voidRatioEGiven', 'effectiveGrainSizeD10',
    'grainSizeD30', 'grainSizeD60', 'coefficientOfLateralEarthPressureAtRestK0', 'notes'
];

const EARTH_PRESSURES_DATA_PROPS = [
    'id', 'uid', 'sigmaZGeosTotTop', 'sigmaZGeosTotIncrement', 'sigmaZGeosTotBottom', 'sigmaZLoadTop',
    'sigmaZLoadIncrement', 'sigmaZLoadBottom', 'sigmaZTotTop', 'sigmaZTotIncrement', 'sigmaZTotBottom',
    'sigmaZGeosEffTop', 'sigmaZGeosEffIncrement', 'sigmaZGeosEffBottom', 'sigmaZEffTop', 'sigmaZEffIncrement',
    'sigmaZEffBottom', 'sigmaXLoadTop', 'sigmaXLoadIncrement', 'sigmaXLoadBottom', 'waterIntervalPressureUxTop',
    'waterIntervalPressureUxIncrement', 'waterIntervalPressureUxBottom', 'sigmaXGeosEffTop', 'sigmaXGeosEffIncrement',
    'sigmaXGeosEffBottom', 'sigmaXGeosTotTop', 'sigmaXGeosTotIncrement', 'sigmaXGeosTotBottom', 'sigmaXEffTop',
    'sigmaXEffIncrement', 'sigmaXEffBottom', 'sigmaXTotTop', 'sigmaXTotIncrement', 'sigmaXTotBottom'
];

const FACE_PRESSURES_CALCULATION_PROPS = [
    'id', 'uid', 'testingStartAngleZetaCrit', 'testingEndAngleZetaCrit', 'testingAngleIncrementZetaCrit',
    'earthPressureSafetyCoefficientAgainstFaceCollapse', 'earthPressureSafetyCoefficientAgainstBlowUp',
    'groundwaterPressureSafetyCoefficientAgainstFaceCollapse', 'groundwaterPressureSafetyCoefficientAgainstBlowUp',
    'maximumFacePressureDeviations', 'safetyFactorForMaxFacePressure', 'slurryUnitWeight',
    'surchargeForMinimumRequiredPressureOnly'
];

const FACE_PRESSURES_GEOTECH_DATA_PROPS = [
    'id', 'uid', 'tunnelFaceAngle', 'tbmElevationTop', 'tbmElevationBottom', 'tbmLevelTop', 'tbmLevelBottom',
    'siloElevationTop', 'siloElevationBottom', 'siloVerticalLevelTop', 'siloVerticalLevelBottom', 'overburden',
    'slidingWedgeElevationBottom', 'slidingWedgeHeight', 'hwCrown', 'unitWeightSoilGama1Average', 'cohesion1Average',
    'internalFrictionAngle1Average', 'unitWeightSoilGama1MinAverage', 'unitWeightWaterGamaW1Average',
    'unitWeightSoilGama2Average', 'cohesion2Average', 'internalFrictionAngle2Average', 'k02Average', 'ka2Average',
    'k1Average', 'k2Average', 'unitWeightWaterGamaW2Average', 'unitWeightSoilGama2MinAverage',
    'maximumAllowableFacePressureSafetyFactor'
];

const FACE_PRESSURES_FORCES_DATA_PROPS = [
    'id', 'uid', 'updatedAt', 'recalculatedAt', 'wedgeSlidingAngleZeta', 'rectangularAreaRequiredSupportEarthForceEre',
    'slidingWedgeWeightG', 'verticalLoadFromSolidPrismPv', 'verticalSlipSurfaceShearForceFromCohesionTc',
    'verticalSlipSurfaceShearForceFromFrictionTr', 'verticalSlipSurfaceShearForceFromFrictionCohesionT',
    'rectangularAreaRequiredSupportWaterForceWre', 'tunnelFaceRequiredSupportWaterForceWci',
    'tunnelFaceRequiredSupportEarthForceEmaxci', 'requiredMinimumSupportForceAtTunnelAxisFaxis1',
    'tunnelFaceRequiredSupportWaterForceInclSafetyCoefWcis', 'tunnelFaceRequiredSupportEarthForceInclSafetyCoefEmaxcis',
    'requiredMinimumSupportForceAtTunnelAxisInclSafetyCoefFaxis2S'
];

const FACE_PRESSURES_FORCES_AT_ANGLE_PROPS = [
    'id', 'uid', 'updatedAt', 'recalculatedAt', 'wedgeSlidingAngleZeta', 'rectangularAreaRequiredSupportEarthForceEre',
    'slidingWedgeWeightG', 'verticalLoadFromSolidPrismPv', 'verticalSlipSurfaceShearForceFromCohesionTc',
    'verticalSlipSurfaceShearForceFromFrictionTr', 'verticalSlipSurfaceShearForceFromFrictionCohesionT'
];

const FACE_PRESSURES_PRESSURES_AT_LEVEL_PROPS = [
    'id', 'uid', 'updatedAt', 'recalculatedAt', 'requiredMinimumSupportForceAtTunnelAxisFaxis1',
    'requiredMinimumSupportPressureP1', 'totalMinimumVerticalStressAtTunnelCrownPcrown1',
    'tunnelFaceRequiredSupportWaterForceInclSafetyCoefWcis', 'tunnelFaceRequiredSupportEarthForceInclSafetyCoefEmaxcis',
    'requiredMinimumSupportForceAtTunnelAxisInclSafetyCoefFaxis2S', 'requiredMinimumSupportPressureInclSafetyCoefP2S',
    'maximumAllowableSupportPressureInclSafetyCoefPmax2S', 'requiredMinimumSupportPressureInclSafetyCoefPrDevP3Sp',
    'maximumAllowableSupportPressureInclSafetyCoefPrDevPmax3Sp', 'waterPressurePwater',
    'finalRequiredMinimumSupportPressureInclSafetyCoefPrDevP4Spf'
];

export class ModelSectionJsonDataManager {

    static deserializeModelSection(data, sourceModelSection = null) {
        const json = normalizeData(data);

        if (json == null)
            return null;

        const model = new ModelSection({
            id: json.id ?? sourceModelSection?.id ?? null,
            uid: json.uid ?? sourceModelSection?.uid ?? null,
            name: json.name ?? sourceModelSection?.name ?? null,
            project: sourceModelSection?.project ?? null,
            notes: json.notes ?? sourceModelSection?.notes ?? null,
        });

        model.geoSection = fromJsonGeoSection(json.geoSection, model);
        if (json.geoSection != null && model.geoSection == null)
            throw new Error(`ModelSection '${model.name}' has invalid GeoSection.`);

        model.loads = mapArray(json.loads, (x) => fromJsonGeoLoad(x, model));
        model.objects = mapArray(json.objects, (x) => fromJsonGeoObject(x, model));
        model.boundaries = mapArray(json.boundaries, (x) => fromJsonGeoBoundary(x, model));
        model.intervals = mapArray(json.intervals, (x) => fromJsonModelInterval(x, model));

        model.earthPressuresCalculation = fromJsonEarthPressuresCalculation(json.earthPressuresCalculation, model);
        if (json.earthPressuresCalculation != null && model.earthPressuresCalculation == null)
            throw new Error(`ModelSection '${model.name}' has invalid EarthPressuresCalculation.`);

        model.facePressuresCalculation = fromJsonFacePressuresCalculation(json.facePressuresCalculation, model);
        if (json.facePressuresCalculation != null && model.facePressuresCalculation == null)
            throw new Error(`ModelSection '${model.name}' has invalid FacePressuresCalculation.`);

        return model;
    }

    static serializeModelSection(model) {
        if (!model)
            return null;

        return {
            id: model.id ?? null,
            uid: model.uid ?? null,
            name: model.name ?? null,
            projectId: idOf(model.project),
            geoSection: toJsonGeoSection(model.geoSection),
            notes: model.notes ?? null,
            loads: mapArray(model.loads, toJsonGeoLoad),
            objects: mapArray(model.objects, toJsonGeoObject),
            boundaries: mapArray(model.boundaries, toJsonGeoBoundary),
            intervals: mapArray(model.intervals, toJsonModelInterval),
            earthPressuresCalculation: toJsonEarthPressuresCalculation(model.earthPressuresCalculation),
            facePressuresCalculation: toJsonFacePressuresCalculation(model.facePressuresCalculation),
        };
    }

}

function normalizeData(data) {
    if (data === null || data === undefined || data === '')
        return null;
    if (typeof data === 'string')
        return JSON.parse(data);
    if (typeof data === 'object')
        return data;
    throw new Error('Model section data must be a JSON string or object.');
}

function fromJsonGeoSection(json, modelSection) {
    if (!json) return null;
    const model = new GeoSection(modelSection, pick(json, ['id', 'uid', 'name', 'elevationOfZeroDatumLevel', 'notes']));
    model.geoParams = fromJsonGeoParams(json.geoParams, model);
    if (json.geoParams != null && model.geoParams == null)
        throw new Error(`GeoSection '${model.name}' has invalid GeoParams.`);
    model.geoTypes = mapArray(json.geoTypes, (x) => fromJsonGeoType(x, model));
    model.waterBodies = mapArray(json.waterBodies, (x) => fromJsonWaterBody(x, model));
    for (const jsonWaterBody of arrayOrEmpty(json.waterBodies)) {
        const waterBody = findById(model.waterBodies, jsonWaterBody.id);
        if (!waterBody)
            throw new Error(`GeoSection '${model.name}' has invalid WaterBody with Id = ${jsonWaterBody.id}.`);
        waterBody.waterBodyAbove = findById(model.waterBodies, jsonWaterBody.waterBodyAboveId);
        waterBody.waterBodyBelow = findById(model.waterBodies, jsonWaterBody.waterBodyBelowId);
    }
    model.layers = mapArray(json.layers, (x) => fromJsonGeoLayer(x, model));
    return model;
}

function toJsonGeoSection(model) {
    if (!model) return null;
    return {
        id: model.id ?? null,
        uid: model.uid ?? null,
        name: model.name ?? null,
        elevationOfZeroDatumLevel: model.elevationOfZeroDatumLevel ?? null,
        notes: model.notes ?? null,
        geoParams: toJsonGeoParams(model.geoParams),
        geoTypes: mapArray(model.geoTypes, toJsonGeoType),
        layers: mapArray(model.layers, toJsonGeoLayer),
        waterBodies: mapArray(model.waterBodies, toJsonWaterBody),
    };
}

function fromJsonGeoParams(json, geoSection) {
    return json ? new GeoParams(geoSection, pick(json, ['id', 'uid', 'gravitationalAccelerationG', 'densityOfWaterRoWGeoTypesPurposes', 'unitWeightOfWaterGamaWGeoTypesPurposes'])) : null;
}

function toJsonGeoParams(model) {
    return model ? pick(model, ['id', 'uid', 'gravitationalAccelerationG', 'densityOfWaterRoWGeoTypesPurposes', 'unitWeightOfWaterGamaWGeoTypesPurposes']) : null;
}

function fromJsonGeoLayer(json, geoSection) {
    if (!json) return null;
    const model = new GeoLayer(geoSection, pick(json, ['id', 'uid', 'name', 'elevationTop', 'elevationBottom', 'verticalLevelTop', 'verticalLevelBottom', 'thickness', 'notes']));
    model.geoType = findById(geoSection.geoTypes, json.geoTypeId);
    requireRef(json.geoTypeId, model.geoType, `GeoLayer '${model.name}' refers to a non-existent GeoType with Id = ${json.geoTypeId}.`);
    return model;
}

function toJsonGeoLayer(model) {
    if (!model) return null;
    return { ...pick(model, ['id', 'uid', 'name', 'elevationTop', 'elevationBottom', 'verticalLevelTop', 'verticalLevelBottom', 'thickness', 'notes']), geoTypeId: idOf(model.geoType) };
}

function fromJsonGeoType(json, geoSection) {
    if (!json) return null;
    return new GeoType(geoSection, {
        ...pick(json, GEO_TYPE_PROPS),
        codeStandardDocument: refOrNull(json.codeStandardDocumentId),
        massType: refOrNull(json.massTypeId),
        bulkUnitWeightGamaCalcMethod: refOrNull(json.bulkUnitWeightGamaCalcMethodId),
        minBulkUnitWeightGamaMinCalcMethod: refOrNull(json.minBulkUnitWeightGamaMinCalcMethodId),
        dryUnitWeightGamaDCalcMethod: refOrNull(json.dryUnitWeightGamaDCalcMethodId),
        unitWeightOfSolidsGamaSCalcMethod: refOrNull(json.unitWeightOfSolidsGamaSCalcMethodId),
        minUnitWeightOfSolidsGamaSMinCalcMethod: refOrNull(json.minUnitWeightOfSolidsGamaSMinCalcMethodId),
        saturatedUnitWeightGamaSatCalcMethod: refOrNull(json.saturatedUnitWeightGamaSatCalcMethodId),
        submergedUnitWeightGamaSubCalcMethod: refOrNull(json.submergedUnitWeightGamaSubCalcMethodId),
        minSubmergedUnitWeightGamaSubMinCalcMethod: refOrNull(json.minSubmergedUnitWeightGamaSubMinCalcMethodId),
        degreeOfSaturationSrCalcMethod: refOrNull(json.degreeOfSaturationSrCalcMethodId),
        moistureContentWCalcMethod: refOrNull(json.moistureContentWCalcMethodId),
        plasticityIndexIpCalcMethod: refOrNull(json.plasticityIndexIpCalcMethodId),
        porosityNCalcMethod: refOrNull(json.porosityNCalcMethodId),
        voidRatioECalcMethod: refOrNull(json.voidRatioECalcMethodId),
        k0CalcMethod: refOrNull(json.k0CalcMethodId),
    });
}

function toJsonGeoType(model) {
    if (!model) return null;
    return {
        ...pick(model, GEO_TYPE_PROPS),
        codeStandardDocumentId: idOf(model.codeStandardDocument),
        massTypeId: idOf(model.massType),
        bulkUnitWeightGamaCalcMethodId: idOf(model.bulkUnitWeightGamaCalcMethod),
        minBulkUnitWeightGamaMinCalcMethodId: idOf(model.minBulkUnitWeightGamaMinCalcMethod),
        dryUnitWeightGamaDCalcMethodId: idOf(model.dryUnitWeightGamaDCalcMethod),
        unitWeightOfSolidsGamaSCalcMethodId: idOf(model.unitWeightOfSolidsGamaSCalcMethod),
        minUnitWeightOfSolidsGamaSMinCalcMethodId: idOf(model.minUnitWeightOfSolidsGamaSMinCalcMethod),
        saturatedUnitWeightGamaSatCalcMethodId: idOf(model.saturatedUnitWeightGamaSatCalcMethod),
        submergedUnitWeightGamaSubCalcMethodId: idOf(model.submergedUnitWeightGamaSubCalcMethod),
        minSubmergedUnitWeightGamaSubMinCalcMethodId: idOf(model.minSubmergedUnitWeightGamaSubMinCalcMethod),
        degreeOfSaturationSrCalcMethodId: idOf(model.degreeOfSaturationSrCalcMethod),
        moistureContentWCalcMethodId: idOf(model.moistureContentWCalcMethod),
        plasticityIndexIpCalcMethodId: idOf(model.plasticityIndexIpCalcMethod),
        porosityNCalcMethodId: idOf(model.porosityNCalcMethod),
        voidRatioECalcMethodId: idOf(model.voidRatioECalcMethod),
        k0CalcMethodId: idOf(model.k0CalcMethod),
    };
}

function fromJsonWaterBody(json, geoSection) {
    if (!json) return null;
    return new WaterBody(geoSection, {
        ...pick(json, ['id', 'uid', 'name', 'elevationTop', 'elevationBottom', 'verticalLevelTop', 'verticalLevelBottom', 'thickness', 'waterUnitWeightGamaW', 'poissonCoefficientNiWater', 'coefficientOfSidePressureKWater', 'waterSurfaceStress', 'waterSurfaceStressGiven', 'piezometricHead', 'piezometricHeadGiven', 'hasWaterBodyAboveRelation', 'additionalStressTop', 'hasWaterBodyBelowRelation', 'additionalStressBottom', 'additionalStressEffectiveTop', 'additionalStressEffectiveBottom', 'waterBodyPressureIncreaseCoefKzInc', 'waterBodyPressureUzIncrease', 'waterBodyPressureUzTop', 'waterBodyPressureUzBottom', 'notes']),
        waterBodyType: refOrNull(json.waterBodyTypeId),
        waterSurfaceStressCalcMethod: refOrNull(json.waterSurfaceStressCalcMethodId),
        piezometricHeadCalcMethod: refOrNull(json.piezometricHeadCalcMethodId),
    });
}

function toJsonWaterBody(model) {
    if (!model) return null;
    return {
        ...pick(model, ['id', 'uid', 'name', 'elevationTop', 'elevationBottom', 'verticalLevelTop', 'verticalLevelBottom', 'thickness', 'waterUnitWeightGamaW', 'poissonCoefficientNiWater', 'coefficientOfSidePressureKWater', 'waterSurfaceStress', 'waterSurfaceStressGiven', 'piezometricHead', 'piezometricHeadGiven', 'hasWaterBodyAboveRelation', 'additionalStressTop', 'hasWaterBodyBelowRelation', 'additionalStressBottom', 'additionalStressEffectiveTop', 'additionalStressEffectiveBottom', 'waterBodyPressureIncreaseCoefKzInc', 'waterBodyPressureUzIncrease', 'waterBodyPressureUzTop', 'waterBodyPressureUzBottom', 'notes']),
        waterBodyTypeId: idOf(model.waterBodyType),
        waterSurfaceStressCalcMethodId: idOf(model.waterSurfaceStressCalcMethod),
        piezometricHeadCalcMethodId: idOf(model.piezometricHeadCalcMethod),
        waterBodyAboveId: idOf(model.waterBodyAbove),
        waterBodyBelowId: idOf(model.waterBodyBelow),
    };
}

function fromJsonGeoLoad(json, modelSection) {
    return json ? new GeoLoad(modelSection, pick(json, ['id', 'uid', 'name', 'elevation', 'continuousUniformVerticalLoadP', 'direction', 'notes'])) : null;
}

function toJsonGeoLoad(model) {
    return model ? { ...pick(model, ['id', 'uid', 'name', 'elevation', 'continuousUniformVerticalLoadP', 'direction', 'notes']), modelSectionId: idOf(model.modelSection) } : null;
}

function fromJsonGeoBoundary(json, modelSection) {
    return json ? new GeoBoundary(modelSection, { ...pick(json, ['id', 'uid', 'name', 'elevation', 'notes']), boundaryType: refOrNull(json.boundaryTypeId) }) : null;
}

function toJsonGeoBoundary(model) {
    return model ? { ...pick(model, ['id', 'uid', 'name', 'elevation', 'notes']), modelSectionId: idOf(model.modelSection), boundaryTypeId: idOf(model.boundaryType) } : null;
}

function fromJsonGeoObject(json, modelSection) {
    if (!json) return null;
    if (json.objectType === TBM_OBJECT_TYPE || json.sensors !== undefined || json.levels !== undefined) {
        const model = new GeoObjectTbm(modelSection, {
            objectType: refOrNull(json.objectTypeId),
            ...pick(json, ['id', 'uid', 'elevationTop', 'elevationBottom', 'thickness', 'verticalLevelTop', 'verticalLevelBottom', 'axisElevation', 'excavationDiameter', 'slope', 'coordinateXStart', 'notes']),
            tunnelOrientationType: refOrNull(json.tunnelOrientationTypeId),
        });
        model.sensors = mapArray(json.sensors, (x) => fromJsonGeoObjectTbmSensor(x, model));
        model.boundaries = mapArray(json.levels, (x) => fromJsonGeoObjectTbmBoundary(x, model));
        return model;
    }
    const model = new GeoObject(modelSection, {
        objectType: refOrNull(json.objectTypeId),
        ...pick(json, ['id', 'uid', 'name', 'elevationTop', 'elevationBottom', 'thickness', 'verticalLevelTop', 'verticalLevelBottom', 'notes']),
    });
    return model;
    //throw new Error(`Conversion from JSON GeoObject '${json.objectType ?? 'unknown'}' to model is not implemented.`);
}

function toJsonGeoObject(model) {
    if (!model) return null;
    if (model instanceof GeoObjectTbm || model.object instanceof GeoObject)
        return toJsonGeoObjectTbm(model);
    throw new Error(`Conversion from model object to JSON is not implemented.`);
}

function toJsonGeoObjectTbm(model) {
    const base = model.object ?? model;
    return {
        objectType: TBM_OBJECT_TYPE,
        ...pick(base, ['id', 'uid', 'name', 'elevationTop', 'elevationBottom', 'thickness', 'verticalLevelTop', 'verticalLevelBottom', 'notes']),
        modelSectionId: idOf(base.modelSection ?? model.modelSection),
        objectTypeId: idOf(base.objectType ?? model.objectType),
        axisElevation: model.axisElevation ?? null,
        excavationDiameter: model.excavationDiameter ?? null,
        slope: model.slope ?? null,
        coordinateXStart: model.coordinateXStart ?? null,
        tunnelOrientationTypeId: idOf(model.tunnelOrientationType),
        sensors: mapArray(model.sensors, toJsonGeoObjectTbmSensor),
        levels: mapArray(model.boundaries, toJsonGeoObjectTbmBoundary),
    };
}

function fromJsonGeoObjectTbmSensor(json, objectTbm) {
    return json ? new GeoObjectTbmSensor(objectTbm, pick(json, ['id', 'uid', 'name', 'levelAboveTbmAxis', 'notes'])) : null;
}

function toJsonGeoObjectTbmSensor(model) {
    return model ? { ...pick(model, ['id', 'uid', 'name', 'levelAboveTbmAxis', 'notes']), objectTbmId: idOf(model.objectTbm) } : null;
}

function fromJsonGeoObjectTbmBoundary(json, objectTbm) {
    return json ? new GeoObjectTbmBoundary(objectTbm, {
        ...pick(json, ['id', 'uid', 'name', 'levelAboveTbmAxis', 'elevation', 'levelAboveTbmAxisGiven', 'notes', 'requiredMinimumSupportForceAtTunnelAxisFaxis1CalcEnabled', 'requiredMinimumSupportPressureP1CalcEnabled', 'totalMinimumVerticalStressAtTunnelCrownPcrown1CalcEnabled', 'tunnelFaceRequiredSupportWaterForceInclSafetyCoefWcisCalcEnabled', 'tunnelFaceRequiredSupportEarthForceInclSafetyCoefEmaxcisCalcEnabled', 'requiredMinimumSupportForceAtTunnelAxisInclSafetyCoefFaxis2SCalcEnabled']),
        boudaryType: refOrNull(json.boundaryTypeId),
    }) : null;
}

function toJsonGeoObjectTbmBoundary(model) {
    return model ? {
        ...pick(model, ['id', 'uid', 'name', 'levelAboveTbmAxis', 'elevation', 'levelAboveTbmAxisGiven', 'notes', 'requiredMinimumSupportForceAtTunnelAxisFaxis1CalcEnabled', 'requiredMinimumSupportPressureP1CalcEnabled', 'totalMinimumVerticalStressAtTunnelCrownPcrown1CalcEnabled', 'tunnelFaceRequiredSupportWaterForceInclSafetyCoefWcisCalcEnabled', 'tunnelFaceRequiredSupportEarthForceInclSafetyCoefEmaxcisCalcEnabled', 'requiredMinimumSupportForceAtTunnelAxisInclSafetyCoefFaxis2SCalcEnabled']),
        objectTbmId: idOf(model.objectTbm),
        boundaryTypeId: idOf(model.boudaryType ?? model.boundaryType),
        pressureSensorId: idOf(model.pressureSensor),
    } : null;
}

function fromJsonModelInterval(json, modelSection) {
    if (!json) return null;
    const model = new ModelInterval(modelSection, pick(json, ['id', 'uid', 'name', 'description', 'notes', 'elevationTop', 'elevationBottom', 'verticalLevelTop', 'verticalLevelBottom', 'thickness', 'isModelSectionTopInterval', 'isModelSectionBottomInterval', 'isWithinWaterBody', 'hasBuoyancyEffectWhenUnderWater', 'isWithinWaterBodyAndHasBuoyancyEffect', 'waterIntervalTopDepth', 'waterIntervalBottomDepth', 'isWaterBodySurface', 'waterIntervalPressureUzTop', 'waterIntervalIncreaseUzInc', 'waterIntervalPressureUzBottom', 'isModelSectionMassTopInterval']));
    model.geoSection = modelSection.geoSection;
    model.geoLayer = findById(modelSection.geoSection?.layers, json.geoLayerId);
    requireRef(json.geoLayerId, model.geoLayer, `ModelInterval with Id = ${model.id} refers to a non-existent GeoLayer with Id = ${json.geoLayerId}.`);
    model.waterBody = findById(modelSection.geoSection?.waterBodies, json.waterBodyId);
    requireRef(json.waterBodyId, model.waterBody, `ModelInterval with Id = ${model.id} refers to a non-existent WaterBody with Id = ${json.waterBodyId}.`);
    model.load = findById(modelSection.loads, json.loadId);
    requireRef(json.loadId, model.load, `ModelInterval with Id = ${model.id} refers to a non-existent GeoLoad with Id = ${json.loadId}.`);
    model.boundary = findById(modelSection.boundaries, json.boundaryId);
    requireRef(json.boundaryId, model.boundary, `ModelInterval with Id = ${model.id} refers to a non-existent GeoBoundary with Id = ${json.boundaryId}.`);
    model.object = findById(modelSection.objects, json.objectId);
    requireRef(json.objectId, model.object, `ModelInterval with Id = ${model.id} refers to a non-existent GeoObject with Id = ${json.objectId}.`);
    return model;
}

function toJsonModelInterval(model) {
    if (!model) return null;
    return {
        ...pick(model, ['id', 'uid', 'name', 'description', 'notes', 'elevationTop', 'elevationBottom', 'verticalLevelTop', 'verticalLevelBottom', 'thickness', 'isModelSectionTopInterval', 'isModelSectionBottomInterval', 'isWithinWaterBody', 'hasBuoyancyEffectWhenUnderWater', 'isWithinWaterBodyAndHasBuoyancyEffect', 'waterIntervalTopDepth', 'waterIntervalBottomDepth', 'isWaterBodySurface', 'waterIntervalPressureUzTop', 'waterIntervalIncreaseUzInc', 'waterIntervalPressureUzBottom', 'isModelSectionMassTopInterval']),
        geoSectionId: idOf(model.geoSection ?? model.modelSection?.geoSection),
        geoLayerId: idOf(model.geoLayer),
        waterBodyId: idOf(model.waterBody),
        loadId: idOf(model.load),
        boundaryId: idOf(model.boundary),
        objectId: idOf(model.object),
    };
}

function fromJsonEarthPressuresCalculation(json, modelSection) {
    if (!json) return null;
    const model = new EarthPressuresCalculation(modelSection, pick(json, ['id', 'uid']));
    model.intervals = mapArray(json.intervals, (x) => fromJsonEarthPressuresModelInterval(x, model));
    model.pressures = mapArray(json.pressures, (x) => fromJsonEarthPressuresData(x, model));
    return model;
}

function toJsonEarthPressuresCalculation(model) {
    return model ? { id: model.id ?? null, uid: model.uid ?? null, intervals: mapArray(model.intervals, toJsonEarthPressuresModelInterval), pressures: mapArray(model.pressures, toJsonEarthPressuresData) } : null;
}

function fromJsonEarthPressuresModelInterval(json, calculation) {
    if (!json) return null;
    const model = new EarthPressuresModelInterval(calculation, { ...pick(json, ['id', 'uid', 'soilUnitWeightGama']), soilUnitWeightGamaCalcMethod: refOrNull(json.soilUnitWeightGamaCalcMethodId) });
    model.modelInterval = findById(calculation.modelSection?.intervals, json.modelIntervalId);
    requireRef(json.modelIntervalId, model.modelInterval, `EarthPressuresModelInterval with Id = ${model.id} refers to a non-existent ModelInterval with Id = ${json.modelIntervalId}.`);
    return model;
}

function toJsonEarthPressuresModelInterval(model) {
    return model ? { ...pick(model, ['id', 'uid', 'soilUnitWeightGama']), modelIntervalId: idOf(model.modelInterval), soilUnitWeightGamaCalcMethodId: idOf(model.soilUnitWeightGamaCalcMethod) } : null;
}

function fromJsonEarthPressuresData(json, calculation) {
    if (!json) return null;
    const model = new EarthPressuresData(calculation, pick(json, EARTH_PRESSURES_DATA_PROPS));
    model.earthPressuresModelInterval = findById(calculation.intervals, json.earthPressuresModelIntervalId);
    requireRef(json.earthPressuresModelIntervalId, model.earthPressuresModelInterval, `EarthPressuresData with Id = ${model.id} refers to a non-existent EarthPressuresModelInterval with Id = ${json.earthPressuresModelIntervalId}.`);
    return model;
}

function toJsonEarthPressuresData(model) {
    return model ? { ...pick(model, EARTH_PRESSURES_DATA_PROPS), earthPressuresModelIntervalId: idOf(model.earthPressuresModelInterval) } : null;
}

function fromJsonFacePressuresCalculation(json, modelSection) {
    if (!json) return null;
    const model = new FacePressuresCalculation(modelSection, {
        ...pick(json, FACE_PRESSURES_CALCULATION_PROPS),
        k0CalcMethod: refOrNull(json.k0CalcMethodId),
        kaCalcMethod: refOrNull(json.kaCalcMethodId),
        k1CalcMethod: refOrNull(json.k1CalcMethodId),
        k2CalcMethod: refOrNull(json.k2CalcMethodId),
        trCalcMethod: refOrNull(json.trCalcMethodId),
    });
    model.updatedAt = json.updatedAt ?? null;
    model.recalculatedAt = json.recalculatedAt ?? null;
    model.intervals = mapArray(json.intervals, (x) => fromJsonFacePressuresModelInterval(x, model));
    model.geotechData = fromJsonFacePressuresGeotechData(json.geotechData, model);
    model.forcesData = fromJsonFacePressuresForcesData(json.forcesData, model);
    model.forcesAtAngles = mapArray(json.forcesAtAngles, (x) => fromJsonFacePressuresForcesAtAngle(x, model));
    model.pressuresAtLevels = mapArray(json.pressuresAtLevels, (x) => fromJsonFacePressuresPressuresAtLevel(x, model));
    return model;
}

function toJsonFacePressuresCalculation(model) {
    return model ? {
        ...pick(model, FACE_PRESSURES_CALCULATION_PROPS),
        updatedAt: model.updatedAt ?? null,
        recalculatedAt: model.recalculatedAt ?? null,
        modelSectionId: idOf(model.modelSection),
        k0CalcMethodId: idOf(model.k0CalcMethod),
        kaCalcMethodId: idOf(model.kaCalcMethod),
        k1CalcMethodId: idOf(model.k1CalcMethod),
        k2CalcMethodId: idOf(model.k2CalcMethod),
        trCalcMethodId: idOf(model.trCalcMethod),
        intervals: mapArray(model.intervals, toJsonFacePressuresModelInterval),
        geotechData: toJsonFacePressuresGeotechData(model.geotechData),
        forcesData: toJsonFacePressuresForcesData(model.forcesData),
        forcesAtAngles: mapArray(model.forcesAtAngles, toJsonFacePressuresForcesAtAngle),
        pressuresAtLevels: mapArray(model.pressuresAtLevels, toJsonFacePressuresPressuresAtLevel),
    } : null;
}

function fromJsonFacePressuresModelInterval(json, calculation) {
    if (!json) return null;
    const model = new FacePressuresModelInterval(calculation, { ...pick(json, ['id', 'uid', 'soilUnitWeightGama']), soilUnitWeightGamaCalcMethod: refOrNull(json.soilUnitWeightGamaCalcMethodId) });
    model.modelInterval = findById(calculation.modelSection?.intervals, json.modelIntervalId);
    requireRef(json.modelIntervalId, model.modelInterval, `FacePressuresModelInterval with Id = ${model.id} refers to a non-existent ModelInterval with Id = ${json.modelIntervalId}.`);
    return model;
}

function toJsonFacePressuresModelInterval(model) {
    return model ? { ...pick(model, ['id', 'uid', 'soilUnitWeightGama']), modelIntervalId: idOf(model.modelInterval), soilUnitWeightGamaCalcMethodId: idOf(model.soilUnitWeightGamaCalcMethod) } : null;
}

function fromJsonFacePressuresGeotechData(json, calculation) {
    if (!json) return null;
    const model = new FacePressuresGeotechData(calculation, pick(json, FACE_PRESSURES_GEOTECH_DATA_PROPS));
    model.object = findTbmById(calculation.modelSection?.objects, json.objectId);
    requireRef(json.objectId, model.object, `FacePressuresGeotechData with Id = ${model.id} refers to a non-existent GeoObjectTbm with Id = ${json.objectId}.`);
    return model;
}

function toJsonFacePressuresGeotechData(model) {
    return model ? { ...pick(model, FACE_PRESSURES_GEOTECH_DATA_PROPS), objectId: idOf(model.object) } : null;
}

function fromJsonFacePressuresForcesData(json, calculation) {
    if (!json) return null;
    const model = new FacePressuresForcesData(calculation, pick(json, FACE_PRESSURES_FORCES_DATA_PROPS));
    model.updatedAt = json.updatedAt ?? null;
    model.recalculatedAt = json.recalculatedAt ?? null;
    return model;
}

function toJsonFacePressuresForcesData(model) {
    return model ? pick(model, FACE_PRESSURES_FORCES_DATA_PROPS) : null;
}

function fromJsonFacePressuresForcesAtAngle(json, calculation) {
    if (!json) return null;
    const model = new FacePressuresForcesAtAngle(calculation, pick(json, FACE_PRESSURES_FORCES_AT_ANGLE_PROPS));
    model.updatedAt = json.updatedAt ?? null;
    model.recalculatedAt = json.recalculatedAt ?? null;
    return model;
}

function toJsonFacePressuresForcesAtAngle(model) {
    return model ? pick(model, FACE_PRESSURES_FORCES_AT_ANGLE_PROPS) : null;
}

function fromJsonFacePressuresPressuresAtLevel(json, calculation) {
    if (!json) return null;
    const model = new FacePressuresPressuresAtLevel(calculation, pick(json, FACE_PRESSURES_PRESSURES_AT_LEVEL_PROPS));
    model.updatedAt = json.updatedAt ?? null;
    model.recalculatedAt = json.recalculatedAt ?? null;
    model.facePressuresModelInterval = findById(calculation.intervals, json.facePressuresModelIntervalId);
    requireRef(json.facePressuresModelIntervalId, model.facePressuresModelInterval, `FacePressuresPressuresAtLevel with Id = ${model.id} refers to a non-existent FacePressuresModelInterval with Id = ${json.facePressuresModelIntervalId}.`);
    model.tbmBoundary = findTbmBoundaryById(calculation.modelSection?.objects, json.tbmBoundaryId);
    requireRef(json.tbmBoundaryId, model.tbmBoundary, `FacePressuresPressuresAtLevel with Id = ${model.id} refers to a non-existent TbmLevel with Id = ${json.tbmBoundaryId}.`);
    return model;
}

function toJsonFacePressuresPressuresAtLevel(model) {
    return model ? { ...pick(model, FACE_PRESSURES_PRESSURES_AT_LEVEL_PROPS), facePressuresModelIntervalId: idOf(model.facePressuresModelInterval), tbmBoundaryId: idOf(model.tbmBoundary) } : null;
}

function pick(source, properties) {
    const result = {};
    for (const property of properties)
        result[property] = source?.[property] ?? null;
    return result;
}

function mapArray(items, mapper) {
    return arrayOrEmpty(items).map(mapper).filter((x) => x != null);
}

function arrayOrEmpty(value) {
    return Array.isArray(value) ? value : [];
}

function findById(items, id) {
    if (id === null || id === undefined)
        return null;
    return arrayOrEmpty(items).find((item) => item?.id === id) ?? null;
}

function findTbmById(items, id) {
    const object = findById(items, id);
    return object instanceof GeoObjectTbm || object?.object instanceof GeoObject ? object : null;
}

function findTbmBoundaryById(items, id) {
    if (id === null || id === undefined)
        return null;
    for (const object of arrayOrEmpty(items)) {
        const boundary = arrayOrEmpty(object?.boundaries).find((x) => x?.id === id);
        if (boundary)
            return boundary;
    }
    return null;
}

function requireRef(id, value, message) {
    if (id !== null && id !== undefined && value == null)
        throw new Error(message);
}

function refOrNull(id) {
    return id === null || id === undefined ? null : { id };
}

function idOf(value) {
    if (value === null || value === undefined)
        return null;
    if (typeof value === 'number' || typeof value === 'string')
        return value;
    return value.id ?? null;
}
